import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiX } from 'react-icons/fi';

/**
 * Full-screen camera barcode scanner for mobile POS. Uses the browser's native
 * BarcodeDetector (Android Chrome / Edge) — zero dependency. Scans CONTINUOUSLY
 * with a short cooldown so the cashier can rack up several items without closing;
 * each detected code is passed to onDetected (which adds it to the cart exactly
 * like the hardware scanner). Gracefully degrades with a message on browsers that
 * don't support BarcodeDetector (e.g. iOS Safari today) — the search box / a
 * hardware scanner still work there.
 *
 * Self-contained and isolated from POSScreen so it can't affect the sale/save
 * path. Always stops the camera stream on close/unmount.
 */
const POSCameraScanner = ({ onDetected, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef({ code: '', ts: 0 });
  const [error, setError] = useState('');
  // Shown on screen so the cashier can confirm (a) the new build is live in the
  // PWA and (b) which lens was chosen (should be the MAIN camera, not 0.5×).
  const [camLabel, setCamLabel] = useState('');
  const [feedback, setFeedback] = useState(null);   // { ok, text } shown briefly on each scan
  const fbTimer = useRef(null);
  const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  useEffect(() => {
    let cancelled = false;

    if (!supported) {
      setError('Camera scanning is not supported on this browser yet. Use the search box or a hardware scanner.');
      return undefined;
    }

    let detector;
    try {
      detector = new window.BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'codabar', 'itf', 'qr_code'],
      });
    } catch {
      detector = new window.BarcodeDetector();
    }

    let audioCtx = null;
    const beep = () => {
      try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        const o = audioCtx.createOscillator();
        const g = audioCtx.createGain();
        o.type = 'square'; o.frequency.value = 880; g.gain.value = 0.08;
        o.connect(g); g.connect(audioCtx.destination); o.start();
        setTimeout(() => { try { o.stop(); } catch { /* noop */ } }, 90);
      } catch { /* audio best-effort */ }
    };

    const stop = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (fbTimer.current) clearTimeout(fbTimer.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };

    const RES = { width: { ideal: 1920 }, height: { ideal: 1080 } };
    // Extract the Android "camera2 N" index from a device label — the MAIN rear
    // sensor is almost always the lowest index; ultra-wide/tele are higher.
    const camIndex = (label) => { const m = /camera2?\s*(\d+)/i.exec(label || ''); return m ? Number(m[1]) : 999; };

    const applyFocus = async (stream) => {
      // Force CONTINUOUS autofocus + near/macro bias so the camera keeps
      // re-focusing on the barcode. Best-effort — ignored if unsupported.
      try {
        const track = stream.getVideoTracks()[0];
        const caps = track.getCapabilities ? track.getCapabilities() : {};
        const advanced = [];
        if (caps.focusMode && caps.focusMode.includes('continuous')) advanced.push({ focusMode: 'continuous' });
        if (caps.focusDistance) advanced.push({ focusDistance: caps.focusDistance.min });
        if (advanced.length) await track.applyConstraints({ advanced });
      } catch { /* focus tuning is best-effort */ }
    };

    (async () => {
      try {
        // Ask for the rear camera at high resolution — a low-res feed can't
        // resolve the thin bars of a small barcode.
        let stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, ...RES },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }

        // Multi-camera phones (e.g. Samsung A55) frequently open the ULTRA-WIDE
        // (0.5×) for "environment", which can't focus on a close barcode. Pick
        // the MAIN back camera and switch to it. (Labels are only readable after
        // permission is granted above.)
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const backs = devices.filter(d => d.kind === 'videoinput' && /back|rear|environment/i.test(d.label));
          if (backs.length > 1) {
            const isSecondary = (l) => /wide|ultra|tele|depth|macro|mono/i.test(l);
            const preferred = backs.filter(d => !isSecondary(d.label));
            const chosen = (preferred.length ? preferred : backs)
              .sort((a, b) => camIndex(a.label) - camIndex(b.label))[0];
            const curId = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
            if (chosen && chosen.deviceId && chosen.deviceId !== curId) {
              stream.getTracks().forEach(t => t.stop());
              stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: chosen.deviceId }, ...RES },
                audio: false,
              });
              if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
            }
          }
        } catch { /* fall back to the default camera */ }

        streamRef.current = stream;
        await applyFocus(stream);
        if (!cancelled) {
          const lbl = stream.getVideoTracks()[0]?.label || '';
          setCamLabel(lbl ? `MAIN CAMERA · ${lbl}` : 'MAIN CAMERA');
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }

        const tick = async () => {
          if (cancelled || !videoRef.current) return;
          const v = videoRef.current;
          try {
            // Only run detection once the video actually has frames — detecting
            // on a not-yet-ready element silently yields nothing.
            if (v.readyState >= 2 && v.videoWidth > 0) {
              const codes = await detector.detect(v);
              if (codes && codes.length > 0) {
                const val = codes[0].rawValue;
                // Manual clock: Date.now() is only a debounce window, not stored —
                // the same barcode won't re-fire for 1.5s.
                const now = Date.now();
                if (val && (val !== lastRef.current.code || now - lastRef.current.ts > 1500)) {
                  lastRef.current = { code: val, ts: now };
                  beep();
                  // onDetected may return a result so we can confirm on-screen
                  // (the cart/toast are hidden behind this full-screen overlay).
                  let res;
                  try { res = onDetected(val); } catch { res = null; }
                  const ok = res && typeof res === 'object' ? res.ok !== false : true;
                  const text = (res && typeof res === 'object' && res.message)
                    ? res.message
                    : (typeof res === 'string' ? res : `Scanned ${val}`);
                  if (!cancelled) {
                    setFeedback({ ok, text });
                    if (fbTimer.current) clearTimeout(fbTimer.current);
                    fbTimer.current = setTimeout(() => setFeedback(null), 1400);
                  }
                }
              }
            }
          } catch { /* ignore per-frame detect errors */ }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch {
        setError('Could not open the camera. Please allow camera permission, or use the search box.');
      }
    })();

    return () => { cancelled = true; stop(); };
  }, [supported, onDetected]);

  return (
    <Overlay>
      <Bar>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span>Scan barcode</span>
          {camLabel && !error && (
            <CamTag title={camLabel}>{camLabel}</CamTag>
          )}
        </div>
        <CloseBtn onClick={onClose} aria-label="Close scanner"><FiX size={24} /></CloseBtn>
      </Bar>
      {error ? (
        <ErrBox>{error}</ErrBox>
      ) : (
        <VideoWrap>
          <video ref={videoRef} playsInline muted />
          <Reticle $hit={!!feedback} $ok={feedback ? feedback.ok : true} />
          {feedback && (
            <FeedbackBanner $ok={feedback.ok}>{feedback.text}</FeedbackBanner>
          )}
        </VideoWrap>
      )}
      <Hint>{error ? '' : 'Point the camera at the barcode — items add to the cart automatically'}</Hint>
    </Overlay>
  );
};

export default POSCameraScanner;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 6000;
  background: #000; display: flex; flex-direction: column;
`;
const Bar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; color: #fff; font-weight: 600; font-size: 16px;
  background: rgba(0,0,0,0.6);
`;
const CloseBtn = styled.button`
  background: none; border: none; color: #fff; cursor: pointer; padding: 4px; line-height: 0;
`;
const CamTag = styled.span`
  font-size: 10.5px; font-weight: 600; color: #7CFC98; letter-spacing: 0.3px;
  margin-top: 2px; max-width: 70vw;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;
const VideoWrap = styled.div`
  position: relative; flex: 1; overflow: hidden;
  video { width: 100%; height: 100%; object-fit: cover; }
`;
const Reticle = styled.div`
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: 78%; max-width: 420px; height: 34%;
  border: 3px solid ${p => (p.$hit ? (p.$ok ? '#22C55E' : '#EF4444') : 'rgba(255,255,255,0.9)')};
  border-radius: 14px;
  box-shadow: 0 0 0 100vmax rgba(0,0,0,0.35);
  transition: border-color 0.1s;
`;
const FeedbackBanner = styled.div`
  position: absolute; left: 50%; bottom: 10%; transform: translateX(-50%);
  max-width: 88%; padding: 10px 16px; border-radius: 10px;
  background: ${p => (p.$ok ? 'rgba(34,197,94,0.95)' : 'rgba(239,68,68,0.95)')};
  color: #fff; font-weight: 700; font-size: 14px; text-align: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
`;
const Hint = styled.div`
  color: #eee; text-align: center; padding: 16px; font-size: 13px; background: rgba(0,0,0,0.6);
`;
const ErrBox = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  color: #eee; text-align: center; padding: 30px; font-size: 15px; line-height: 1.6;
`;
